export const CANONICAL_COMPANY_COMPLIANCE_REQUIREMENT_KEYS = Object.freeze([
  "license",
  "insurance",
  "w9",
] as const);

export type CanonicalCompanyComplianceRequirementKey =
  typeof CANONICAL_COMPANY_COMPLIANCE_REQUIREMENT_KEYS[number];

export const CANONICAL_COMPANY_COMPLIANCE_REVIEW_STATES = Object.freeze([
  "pending",
  "approved",
  "rejected",
  "changes_requested",
] as const);

export type CanonicalCompanyComplianceReviewState =
  typeof CANONICAL_COMPANY_COMPLIANCE_REVIEW_STATES[number];

export type CanonicalCompanyComplianceDecision =
  Exclude<CanonicalCompanyComplianceReviewState, "pending">;

export type CanonicalCompanyComplianceReviewerRole =
  | "super_admin"
  | "admin"
  | "ops_manager"
  | "owner"
  | "custom";

export type CanonicalCompanyComplianceQueueItem = Readonly<{
  organizationId: string;
  contractorId: string;
  recordId: string;
  requirementKey: CanonicalCompanyComplianceRequirementKey;
  evidenceId: string | null;
  selectionStatus: string;
  evidenceEligible: boolean;
  reviewState: CanonicalCompanyComplianceReviewState;
  reviewApproved: boolean;
  operationallyVerified: boolean;
  failureReason: string | null;
  latestDecisionId: string | null;
  fileName: string | null;
  mimeType: string | null;
  byteSize: number | null;
  submittedAt: string | null;
  issueDate: string | null;
  expiresAt: string | null;
}>;

export type CanonicalCompanyComplianceQueue = Readonly<{
  items: readonly CanonicalCompanyComplianceQueueItem[];
  nextCursor: string | null;
}>;

export type CanonicalCompanyComplianceDetail = Readonly<{
  organizationId: string;
  contractorId: string;
  recordId: string;
  requirements: readonly CanonicalCompanyComplianceQueueItem[];
}>;

export type CanonicalCompanyComplianceHistoryItem = Readonly<{
  decisionId: string;
  evidenceId: string;
  decision: Exclude<CanonicalCompanyComplianceReviewState, "pending">;
  reasonCode: string | null;
  note: string | null;
  reviewerRole: CanonicalCompanyComplianceReviewerRole;
  reviewerScopeId: string | null;
  previousDecisionId: string | null;
  decidedAt: string;
}>;

export type CanonicalCompanyComplianceHistory = Readonly<{
  evidenceId: string;
  decisions: readonly CanonicalCompanyComplianceHistoryItem[];
}>;

export type CanonicalCompanyComplianceDecisionResult = Readonly<{
  decisionId: string;
  evidenceId: string;
  decision: CanonicalCompanyComplianceDecision;
  reasonCode: string | null;
  note: string | null;
  reviewerUserId: string;
  reviewerScopeId: string | null;
  reviewerRole: CanonicalCompanyComplianceReviewerRole;
  decidedAt: string;
  previousDecisionId: string | null;
  requestId: string | null;
}>;

export type CanonicalCompanyCompliancePreview = Readonly<{
  signedUrl: string;
  expiresAt: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
}>;

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SAFE_IDENTIFIER = /^[a-z][a-z0-9_]{0,63}$/;
const SAFE_IDEMPOTENCY_KEY = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const UNSAFE_NOTE =
  /<[^>]+>|https?:\/\/|authorization\s*:|bearer\s+|service[_ -]?role|refresh[_ -]?token|api[_ -]?key/i;
const REVIEWER_ROLES = new Set<CanonicalCompanyComplianceReviewerRole>([
  "super_admin",
  "admin",
  "ops_manager",
  "owner",
  "custom",
]);
const DECISIONS = new Set(["approved", "rejected", "changes_requested"]);
const QUEUE_KEYS = [
  "organizationId", "contractorId", "recordId", "requirementKey",
  "evidenceId", "selectionStatus", "evidenceEligible", "reviewState",
  "reviewApproved", "operationallyVerified", "failureReason",
  "latestDecisionId", "fileName", "mimeType", "byteSize", "submittedAt",
  "issueDate", "expiresAt",
] as const;

export class CanonicalCompanyComplianceContractError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = "CanonicalCompanyComplianceContractError";
  }
}

function invalid(code = "canonical_company_compliance_response_invalid"): never {
  throw new CanonicalCompanyComplianceContractError(code);
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) invalid();
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): void {
  const allowed = new Set(keys);
  if (
    Object.keys(value).some((key) => !allowed.has(key))
    || keys.some((key) => !(key in value))
  ) {
    invalid();
  }
}

function string(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) invalid();
  return value.trim();
}

function nullableString(value: unknown): string | null {
  return value === null ? null : string(value);
}

function uuid(value: unknown): string {
  const result = string(value);
  if (!UUID.test(result)) invalid("canonical_company_compliance_uuid_invalid");
  return result;
}

function nullableUuid(value: unknown): string | null {
  return value === null ? null : uuid(value);
}

function boolean(value: unknown): boolean {
  if (typeof value !== "boolean") invalid();
  return value;
}

function nullableSize(value: unknown): number | null {
  if (value === null) return null;
  if (
    typeof value !== "number"
    || !Number.isFinite(value)
    || value < 0
  ) {
    invalid();
  }
  return value;
}

export function assertCanonicalCompanyComplianceUuid(value: unknown): string {
  return uuid(value);
}

export function assertCanonicalCompanyComplianceLimit(value: unknown): number {
  if (
    typeof value !== "number"
    || !Number.isInteger(value)
    || value < 1
    || value > 100
  ) {
    invalid("canonical_company_compliance_limit_invalid");
  }
  return value;
}

export function validateCanonicalCompanyComplianceDecisionInput(input: {
  decision: CanonicalCompanyComplianceDecision;
  reasonCode?: string;
  note?: string;
  idempotencyKey: string;
}): Readonly<{
  decision: CanonicalCompanyComplianceDecision;
  reasonCode?: string;
  note?: string;
  idempotencyKey: string;
}> {
  if (!DECISIONS.has(input.decision) || !SAFE_IDEMPOTENCY_KEY.test(input.idempotencyKey)) {
    invalid("canonical_company_compliance_decision_invalid");
  }
  const reasonCode = input.reasonCode?.trim() || undefined;
  if (
    (input.decision === "approved" && reasonCode !== undefined)
    || (input.decision !== "approved"
      && (!reasonCode || !SAFE_IDENTIFIER.test(reasonCode)))
  ) {
    invalid("canonical_company_compliance_reason_invalid");
  }
  const note = input.note?.trim() || undefined;
  if (note && (note.length > 2000 || UNSAFE_NOTE.test(note))) {
    invalid("canonical_company_compliance_note_invalid");
  }
  if (reasonCode === "other" && !note) {
    invalid("canonical_company_compliance_note_invalid");
  }
  return Object.freeze({
    decision: input.decision,
    ...(reasonCode ? { reasonCode } : {}),
    ...(note ? { note } : {}),
    idempotencyKey: input.idempotencyKey,
  });
}

export function parseCanonicalCompanyComplianceQueueItem(
  value: unknown,
): CanonicalCompanyComplianceQueueItem {
  const row = record(value);
  exactKeys(row, QUEUE_KEYS);
  if (
    !CANONICAL_COMPANY_COMPLIANCE_REQUIREMENT_KEYS.includes(
      row.requirementKey as CanonicalCompanyComplianceRequirementKey,
    )
    || !CANONICAL_COMPANY_COMPLIANCE_REVIEW_STATES.includes(
      row.reviewState as CanonicalCompanyComplianceReviewState,
    )
  ) {
    invalid();
  }
  return Object.freeze({
    organizationId: uuid(row.organizationId),
    contractorId: uuid(row.contractorId),
    recordId: uuid(row.recordId),
    requirementKey:
      row.requirementKey as CanonicalCompanyComplianceRequirementKey,
    evidenceId: nullableUuid(row.evidenceId),
    selectionStatus: string(row.selectionStatus),
    evidenceEligible: boolean(row.evidenceEligible),
    reviewState: row.reviewState as CanonicalCompanyComplianceReviewState,
    reviewApproved: boolean(row.reviewApproved),
    operationallyVerified: boolean(row.operationallyVerified),
    failureReason: nullableString(row.failureReason),
    latestDecisionId: nullableUuid(row.latestDecisionId),
    fileName: nullableString(row.fileName),
    mimeType: nullableString(row.mimeType),
    byteSize: nullableSize(row.byteSize),
    submittedAt: nullableString(row.submittedAt),
    issueDate: nullableString(row.issueDate),
    expiresAt: nullableString(row.expiresAt),
  });
}

export function parseCanonicalCompanyComplianceQueue(
  value: unknown,
): CanonicalCompanyComplianceQueue {
  const response = record(value);
  exactKeys(response, ["items", "nextCursor"]);
  if (!Array.isArray(response.items) || response.nextCursor !== null) invalid();
  return Object.freeze({
    items: Object.freeze(
      response.items.map(parseCanonicalCompanyComplianceQueueItem),
    ),
    nextCursor: null,
  });
}

export function parseCanonicalCompanyComplianceDetail(
  value: unknown,
): CanonicalCompanyComplianceDetail {
  const response = record(value);
  exactKeys(response, [
    "organizationId", "contractorId", "recordId", "requirements",
  ]);
  if (!Array.isArray(response.requirements)) invalid();
  const organizationId = uuid(response.organizationId);
  const contractorId = uuid(response.contractorId);
  const recordId = uuid(response.recordId);
  const requirements = response.requirements.map(
    parseCanonicalCompanyComplianceQueueItem,
  );
  const keys = new Set(requirements.map((item) => item.requirementKey));
  if (
    requirements.length !== CANONICAL_COMPANY_COMPLIANCE_REQUIREMENT_KEYS.length
    || keys.size !== CANONICAL_COMPANY_COMPLIANCE_REQUIREMENT_KEYS.length
    || CANONICAL_COMPANY_COMPLIANCE_REQUIREMENT_KEYS.some(
      (key) => !keys.has(key),
    )
    || requirements.some((item) =>
      item.organizationId !== organizationId
      || item.contractorId !== contractorId
      || item.recordId !== recordId
    )
  ) {
    invalid();
  }
  return Object.freeze({
    organizationId,
    contractorId,
    recordId,
    requirements: Object.freeze(requirements),
  });
}

export function parseCanonicalCompanyComplianceHistory(
  value: unknown,
): CanonicalCompanyComplianceHistory {
  const response = record(value);
  exactKeys(response, ["evidenceId", "decisions"]);
  if (!Array.isArray(response.decisions)) invalid();
  const evidenceId = uuid(response.evidenceId);
  const decisions = response.decisions.map((value) => {
    const row = record(value);
    exactKeys(row, [
      "id", "evidence_id", "decision", "reason_code", "note",
      "reviewer_role", "reviewer_scope_id", "supersedes_decision_id",
      "decided_at",
    ]);
    const decision = string(row.decision);
    const reviewerRole = string(row.reviewer_role);
    const reasonCode = nullableString(row.reason_code);
    if (
      !DECISIONS.has(decision)
      || !REVIEWER_ROLES.has(reviewerRole)
      || (reasonCode !== null && !SAFE_IDENTIFIER.test(reasonCode))
    ) {
      invalid();
    }
    return Object.freeze({
      decisionId: uuid(row.id),
      evidenceId: uuid(row.evidence_id),
      decision: decision as CanonicalCompanyComplianceHistoryItem["decision"],
      reasonCode,
      note: nullableString(row.note),
      reviewerRole:
        reviewerRole as CanonicalCompanyComplianceHistoryItem["reviewerRole"],
      reviewerScopeId: nullableUuid(row.reviewer_scope_id),
      previousDecisionId: nullableUuid(row.supersedes_decision_id),
      decidedAt: string(row.decided_at),
    });
  });
  if (
    decisions.some((item) => item.evidenceId !== evidenceId)
    || decisions.some((item, index) =>
      index > 0
      && decisions[index - 1].decidedAt > item.decidedAt
    )
  ) {
    invalid();
  }
  return Object.freeze({
    evidenceId,
    decisions: Object.freeze(decisions),
  });
}

export function parseCanonicalCompanyComplianceDecisionResult(
  value: unknown,
): CanonicalCompanyComplianceDecisionResult {
  const row = record(value);
  exactKeys(row, [
    "decision_event_id", "evidence_id", "decision", "reason_code", "note",
    "reviewer_user_id", "reviewer_scope_id", "reviewer_role", "decided_at",
    "previous_decision_id", "request_id",
  ]);
  const decision = string(row.decision);
  const reviewerRole = string(row.reviewer_role);
  const reasonCode = nullableString(row.reason_code);
  if (
    !DECISIONS.has(decision)
    || !REVIEWER_ROLES.has(reviewerRole)
    || (reasonCode !== null && !SAFE_IDENTIFIER.test(reasonCode))
  ) invalid();
  return Object.freeze({
    decisionId: uuid(row.decision_event_id),
    evidenceId: uuid(row.evidence_id),
    decision: decision as CanonicalCompanyComplianceDecision,
    reasonCode,
    note: nullableString(row.note),
    reviewerUserId: uuid(row.reviewer_user_id),
    reviewerScopeId: nullableUuid(row.reviewer_scope_id),
    reviewerRole:
      reviewerRole as CanonicalCompanyComplianceDecisionResult["reviewerRole"],
    decidedAt: string(row.decided_at),
    previousDecisionId: nullableUuid(row.previous_decision_id),
    requestId: nullableString(row.request_id),
  });
}

export function parseCanonicalCompanyCompliancePreview(
  value: unknown,
): CanonicalCompanyCompliancePreview {
  const row = record(value);
  exactKeys(row, ["signedUrl", "expiresAt", "fileName", "mimeType", "byteSize"]);
  const signedUrl = string(row.signedUrl);
  if (!/^https?:\/\//i.test(signedUrl)) invalid();
  const byteSize = nullableSize(row.byteSize);
  if (byteSize === null) invalid();
  return Object.freeze({
    signedUrl,
    expiresAt: string(row.expiresAt),
    fileName: string(row.fileName),
    mimeType: string(row.mimeType),
    byteSize,
  });
}
